import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8f6f0] py-12 px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#2E4E3F] hover:text-[#1E2B22] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Waste Intelligence Engine</span>
        </button>

        <div className="bg-[#FDFBF7] rounded-3xl border border-[#E6E2D6] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#1E2B22] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Privacy Policy
          </h1>
          
          <p className="text-sm text-[#4A5E51] mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <div className="prose prose-lg max-w-none space-y-8 text-[#4A5E51]">
            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                1. Introduction
              </h2>
              <p className="leading-relaxed">
                Welcome to Waste Intelligence Engine ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services. By using our service, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                2. Information We Collect
              </h2>
              <h3 className="text-xl font-medium text-[#2E4E3F] mb-3">2.1 Information You Provide</h3>
              <p className="leading-relaxed mb-4">
                We collect information you voluntarily provide when using our service:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Waste Type Queries:</strong> The waste materials you search for (e.g., "plastic bottles", "coffee grounds")</li>
                <li><strong>Usage Data:</strong> How you interact with our service</li>
              </ul>

              <h3 className="text-xl font-medium text-[#2E4E3F] mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Log Data:</strong> IP address, browser type, operating system, pages visited, time spent</li>
                <li><strong>Cookies:</strong> We use cookies to enhance user experience and for analytics</li>
                <li><strong>Analytics:</strong> Google Analytics may be used to understand site usage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                3. How We Use Your Information
              </h2>
              <p className="leading-relaxed mb-4">We use collected information for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Providing AI-powered waste transformation research results</li>
                <li>Improving and optimizing our service</li>
                <li>Understanding user behavior and preferences</li>
                <li>Displaying relevant advertisements (Google AdSense)</li>
                <li>Communicating with you about service updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                4. AI and Third-Party Services
              </h2>
              <h3 className="text-xl font-medium text-[#2E4E3F] mb-3">4.1 Claude AI</h3>
              <p className="leading-relaxed mb-4">
                We use Anthropic's Claude AI to generate waste transformation research. Your queries are processed by Claude to provide intelligent, research-backed suggestions. Please refer to Anthropic's privacy policy for information on how they handle data.
              </p>

              <h3 className="text-xl font-medium text-[#2E4E3F] mb-3">4.2 Google AdSense</h3>
              <p className="leading-relaxed mb-4">
                We use Google AdSense to display advertisements. Google may use cookies and web beacons to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" className="text-[#B4654A] hover:underline" target="_blank" rel="noopener noreferrer">Google's Ads Settings</a>.
              </p>

              <h3 className="text-xl font-medium text-[#2E4E3F] mb-3">4.3 Analytics</h3>
              <p className="leading-relaxed">
                We may use Google Analytics to analyze website traffic and user behavior. Google Analytics uses cookies to collect anonymous information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                5. Data Storage and Security
              </h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your information. However, no method of transmission over the internet is 100% secure. We strive to protect your data but cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                6. Cookies Policy
              </h2>
              <p className="leading-relaxed mb-4">
                We use cookies for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Advertising Cookies:</strong> Used by Google AdSense to display relevant ads</li>
              </ul>
              <p className="leading-relaxed mt-4">
                You can control cookies through your browser settings. Disabling cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                7. Your Rights
              </h2>
              <p className="leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Opt-out of personalized advertising</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                8. Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                9. Changes to This Privacy Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                10. Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-[#f8f6f0] rounded-lg border border-[#E6E2D6]">
                <p className="font-medium">Waste Intelligence Engine</p>
                <p className="text-sm mt-2">In partnership with Kaagazz</p>
                <p className="text-sm">Website: <a href="https://ecokaagazz.com" className="text-[#B4654A] hover:underline" target="_blank" rel="noopener noreferrer">ecokaagazz.com</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#2E4E3F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                11. GDPR Compliance (EU Users)
              </h2>
              <p className="leading-relaxed">
                If you are located in the European Economic Area (EEA), you have additional rights under GDPR including the right to data portability, the right to object to processing, and the right to lodge a complaint with a supervisory authority.
              </p>
            </section>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;